import { usePermissions, setViewAsExpert } from '@/hooks/usePermissions'
import { useAuth0 } from '@auth0/auth0-react'
import {
  AppShell,
  Avatar,
  Burger,
  Divider,
  Group,
  NavLink,
  ScrollArea,
  Stack,
  Switch,
  Text,
  Title,
  UnstyledButton,
  Menu,
  rem,
  Image,
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import {
  IconFingerprint,
  IconLogout,
  IconRoute,
  IconSearch,
  IconShieldCheck,
  IconUser,
  IconLayoutDashboard,
  IconSettings,
  IconPackage,
  IconListCheck,
  IconListDetails,
  IconClipboardCheck,
  IconTag,
  IconChartBar,
  IconActivity,
  IconUsersPlus,
  IconBolt,
  IconArrowsExchange,
} from '@tabler/icons-react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useMemo } from 'react'
import { ClientSwitcher } from '@/components/ClientSwitcher'
import { VersionSwitcher } from '@/components/VersionSwitcher'

interface NavItem {
  path: string
  label: string
  icon: typeof IconLayoutDashboard
  section: 'analytics' | 'operations'
  externalVisible?: boolean
}

const navItems: NavItem[] = [
  { path: '/', label: 'Overview', icon: IconLayoutDashboard, section: 'analytics' },
  { path: '/compliance', label: 'Compliance', icon: IconShieldCheck, section: 'analytics' },
  { path: '/offenses', label: 'Offense Records', icon: IconListDetails, section: 'analytics' },
  { path: '/identity', label: 'Identity Verification', icon: IconFingerprint, section: 'analytics' },
  { path: '/routing', label: 'Decision Routing', icon: IconRoute, section: 'analytics' },
  { path: '/search', label: 'Search Analytics', icon: IconSearch, section: 'analytics' },
  { path: '/automation', label: 'Automation', icon: IconPackage, section: 'analytics' },
  { path: '/metering', label: 'API Metering', icon: IconActivity, section: 'analytics' },
  { path: '/ops/feature-qa', label: 'Feature QA', icon: IconListCheck, section: 'operations' },
  { path: '/ops/external-qa', label: 'External QA', icon: IconClipboardCheck, section: 'operations' },
  { path: '/ops/qa-errors', label: 'QA Error Rate', icon: IconChartBar, section: 'operations' },
  { path: '/ops/labeling', label: 'Labeling', icon: IconTag, section: 'operations' },
  { path: '/ops/evaluate', label: 'Evaluate Record', icon: IconBolt, section: 'operations' },
  { path: '/ops/compare-searches', label: 'Compare Searches', icon: IconArrowsExchange, section: 'operations' },
  { path: '/ops/client-onboarding', label: 'Client Onboarding', icon: IconUsersPlus, section: 'operations' },
]

export function Layout() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, logout } = useAuth0()
  const [opened, { toggle, close }] = useDisclosure()
  const {
    isAdmin,
    isExpert,
    isExecutive,
    isExternalUser,
    isActualAdmin,
    viewAsExpert,
    effectiveClientLabel,
  } = usePermissions()

  const handleViewAsExpertToggle = (checked: boolean) => {
    setViewAsExpert(checked)
    // Land on the page experts see, or back on the admin home.
    navigate(checked ? '/offenses' : '/')
  }

  const filteredNavItems = useMemo(() => {
    if (isExternalUser) {
      return navItems.filter(item => item.section === 'analytics' || item.externalVisible)
    }
    if (isExecutive) {
      return navItems.filter(item => item.section === 'analytics')
    }
    if (isExpert) {
      return navItems.filter(item =>
        item.path === '/offenses'
      )
    }
    if (isAdmin) {
      return navItems
    }
    return navItems.filter(item => item.section === 'analytics')
  }, [isAdmin, isExpert, isExecutive, isExternalUser])

  const analyticsItems = filteredNavItems.filter(i => i.section === 'analytics')
  const operationsItems = filteredNavItems.filter(i => i.section === 'operations')

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/'
    return location.pathname.startsWith(path)
  }

  return (
    <AppShell
      header={{ height: 60 }}
      navbar={{ width: 260, breakpoint: 'sm', collapsed: { mobile: !opened } }}
      padding="lg"
    >
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between">
          <Group gap="sm">
            <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" />
            <Image src="/snh-ai-logo.png" alt="SNH AI" h={32} w={32} fit="contain" />
            <Stack gap={0}>
              <Text size="xs" c="dimmed" lh={1}>SNH AI</Text>
              <Title order={5} fw={700} lh={1.2}>Public Records</Title>
            </Stack>
          </Group>

          <Group gap="md">
            {isActualAdmin && (
              <Switch
                size="sm"
                label="Expert View"
                labelPosition="left"
                checked={viewAsExpert}
                onChange={(e) => handleViewAsExpertToggle(e.currentTarget.checked)}
              />
            )}
          <Menu shadow="md" width={220} position="bottom-end">
            <Menu.Target>
              <UnstyledButton>
                <Group gap="xs">
                  <Avatar size="sm" radius="xl" color="brand" src={user?.picture}>
                    {user?.name?.charAt(0) || 'U'}
                  </Avatar>
                  <Text size="sm" fw={500} visibleFrom="sm">
                    {user?.name || 'User'}
                  </Text>
                </Group>
              </UnstyledButton>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Label>{user?.email}</Menu.Label>
              {!isAdmin && (
                <Menu.Item disabled>
                  <Text size="xs" c="dimmed">{effectiveClientLabel}</Text>
                </Menu.Item>
              )}
              <Menu.Item leftSection={<IconUser style={{ width: rem(14), height: rem(14) }} />}>
                Profile
              </Menu.Item>
              {isAdmin && (
                <Menu.Item leftSection={<IconSettings style={{ width: rem(14), height: rem(14) }} />}>
                  Settings
                </Menu.Item>
              )}
              <Menu.Divider />
              <Menu.Item
                color="red"
                leftSection={<IconLogout style={{ width: rem(14), height: rem(14) }} />}
                onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
              >
                Logout
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p="sm">
        <AppShell.Section grow component={ScrollArea}>
          <Stack gap={4}>
            {!isExpert && <ClientSwitcher />}
            {!isExpert && <VersionSwitcher />}

            {!isAdmin && isExternalUser && (
              <Text size="xs" c="dimmed" px="sm" mb="xs">
                {effectiveClientLabel}
              </Text>
            )}

            <Text size="xs" fw={600} c="dimmed" tt="uppercase" px="sm" mb={4}>
              Analytics
            </Text>
            {analyticsItems.map((item) => {
              const Icon = item.icon
              return (
                <NavLink
                  key={item.path}
                  label={item.label}
                  leftSection={<Icon size={18} stroke={1.5} />}
                  active={isActive(item.path)}
                  onClick={() => { navigate(item.path); close() }}
                  variant="light"
                />
              )
            })}

            {operationsItems.length > 0 && (
              <>
                <Divider my="sm" />
                <Text size="xs" fw={600} c="dimmed" tt="uppercase" px="sm" mb={4}>
                  Operations
                </Text>
                {operationsItems.map((item) => {
                  const Icon = item.icon
                  return (
                    <NavLink
                      key={item.path}
                      label={item.label}
                      leftSection={<Icon size={18} stroke={1.5} />}
                      active={isActive(item.path)}
                      onClick={() => { navigate(item.path); close() }}
                      variant="light"
                    />
                  )
                })}
              </>
            )}
          </Stack>
        </AppShell.Section>
      </AppShell.Navbar>

      <AppShell.Main>
        <Outlet />
      </AppShell.Main>
    </AppShell>
  )
}
